import { cn } from "@/lib/utils";

/**
 * Four numbers that matter, as a status ribbon: icon block · value · label · one line of context.
 * Not tiles – no borders, generous spacing, hairlines between on desktop. Each is a quiet button into the relevant view.
 * `items`: [{ id, label, value, sub, icon: LucideIcon, tone?: "danger" | "warning" | "primary", onClick }]
 */
export const StatStrip = ({ items, className, testId = "dashboard-stats", idPrefix = "dashboard-stat" }) => (
  <div className={cn("grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-4 sm:gap-x-0", className)} data-testid={testId}>
    {items.map((it, i) => {
      const Icon = it.icon;
      return (
        <div key={it.id} className={cn("min-w-0", i > 0 && "sm:border-l sm:border-border/70 sm:pl-6")}>
          <button
            type="button"
            onClick={it.onClick}
            className="group -mx-2 -my-2 flex w-[calc(100%+1rem)] items-start gap-3.5 rounded-xl px-2 py-2 text-left transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
            data-testid={`${idPrefix}-${it.id}`}
          >
            {Icon && (
              <span
                className={cn(
                  "mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105 sm:flex",
                  it.tone === "danger" ? "bg-rose-500/10 text-rose-600 dark:text-rose-400" : it.tone === "warning" ? "bg-amber-500/10 text-amber-700 dark:text-amber-300" : it.tone === "primary" ? "bg-primary/10 text-primary" : "bg-muted text-foreground/70"
                )}
                aria-hidden
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className={cn("block text-[24px] font-semibold leading-7 tracking-tight tabular sm:text-[28px] sm:leading-8", it.tone === "danger" && "text-rose-600 dark:text-rose-400", it.tone === "warning" && "text-amber-700 dark:text-amber-300")} data-testid={`${idPrefix}-${it.id}-value`}>
                {it.value}
              </span>
              <span className="block text-[12.5px] leading-4 text-foreground/80">{it.label}</span>
              <span className="mt-0.5 block truncate text-[11.5px] leading-4 text-muted-foreground">{it.sub}</span>
            </span>
          </button>
        </div>
      );
    })}
  </div>
);
